import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { useCatalog } from '../../context/CatalogContext'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import OnThisPage from '../../components/OnThisPage'

type DocViewerProps = {
  // When provided, use this slug instead of deriving from route params
  overrideSlug?: string
}

export default function DocViewer(props: DocViewerProps) {
  // Support nested slugs via splat param "*"
  const params = useParams()
  const routeSlug = (params['*'] as string) || (params as any).slug // compat with the old route
  const slug = props.overrideSlug ?? routeSlug
  const { catalog, loading, error: catError } = useCatalog()
  const [content, setContent] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [redirectTo, setRedirectTo] = useState<string | null>(null)
  const proseRef = useRef<HTMLDivElement>(null)

  // Base to fetch raw Markdown files from the GitHub Wiki at runtime
  const activeWiki = catalog?.wikis?.[0]
  const WIKI_REPO_RAW_BASE = activeWiki?.rawBase || 'https://raw.githubusercontent.com/wiki/NordicScreen/Q-Play_Developer'
  const WIKI_WEB_HOME = activeWiki?.webHome || 'https://github.com/NordicScreen/Q-Play_Developer/wiki'

  function normalizeWikiPage(input: string) {
    const trimmed = (input || '').replace(/^\/+|\/+$/g, '')
    const noMd = trimmed.replace(/\.md$/i, '')
    return noMd.replace(/\s+/g, '-')
  }

  function toWikiFileName(slugLike: string) {
    const name = normalizeWikiPage(slugLike)
    return `${encodeURIComponent(name)}.md`
  }

  async function loadWikiPage(slugLike: string): Promise<{ text: string; url: string } | null> {
    const file = toWikiFileName(slugLike)
    const url = `${WIKI_REPO_RAW_BASE}/${file}`
    try {
      const res = await fetch(url, { cache: 'no-cache' })
      if (!res.ok) return null
      const text = await res.text()
      return { text, url }
    } catch (_e) {
      return null
    }
  }

  // Convert wiki-style links [[Page Name]] into standard Markdown links
  function preprocessWikiSyntax(md: string) {
    if (!md) return md
    return md.replace(/\[\[([^\]]+)]]/g, (_m, p1: string) => {
      const target = normalizeWikiPage(p1)
      return `[${p1}](/docs/${target})`
    })
  }

  // Rewrite absolute GitHub wiki links to in-app routes; returns null if not a wiki URL.
  const rewriteAbsoluteWiki = (url: string): string | null => {
    try {
      const u = new URL(url)
      const host = u.host.toLowerCase()
      const path = u.pathname
      let webOrgRepo: RegExp | null = null
      try {
        const wh = new URL(WIKI_WEB_HOME)
        const m = /^\/(.+?)\/(.+?)\/wiki\/?$/.exec(wh.pathname)
        if (m) webOrgRepo = new RegExp(`^\/${m[1].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/${m[2].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/wiki\/(.+)$`)
      } catch (_e) { /* ignore */ }

      const webMatch = (webOrgRepo || /^\/NordicScreen\/Q-Play_Developer\/wiki\/(.+)$/).exec(path)
      if ((host === 'github.com' || host === 'www.github.com') && webMatch) {
        const page = decodeURIComponent(webMatch[1])
        const s = normalizeWikiPage(page).replace(/\.md$/i, '')
        return `/docs/${s}${u.hash || ''}`
      }
      let rawOrgRepo: RegExp | null = null
      try {
        const rb = new URL(WIKI_REPO_RAW_BASE)
        const m2 = /^\/wiki\/([^/]+)\/([^/]+)\/?$/.exec(rb.pathname)
        if (m2) rawOrgRepo = new RegExp(`^\/wiki\/${m2[1].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/${m2[2].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/(.+)\\.md$`)
      } catch (_e) { /* ignore */ }

      const rawMatch = (rawOrgRepo || /^\/wiki\/NordicScreen\/Q-Play_Developer\/(.+)\.md$/).exec(path)
      if (host === 'raw.githubusercontent.com' && rawMatch) {
        const page = decodeURIComponent(rawMatch[1])
        const s = normalizeWikiPage(page)
        return `/docs/${s}${u.hash || ''}`
      }
    } catch (_e) { /* ignore */ }
    return null
  }

  // decideLink for MarkdownRenderer: keep wiki links inside the app, open other links externally.
  const decideLink = React.useCallback(
    (href: string) => {
      const h = String(href || '')
      if (!h || h.startsWith('#') || h.startsWith('mailto:')) return undefined
      if (/^(https?:)?\/\//i.test(h)) {
        const internal = rewriteAbsoluteWiki(h)
        return internal ? { internalTo: internal } : { externalTo: h }
      }
      const noMd = normalizeWikiPage(h).replace(/\.md$/i, '')
      return { internalTo: `/docs/${noMd}` }
    },
    [WIKI_REPO_RAW_BASE, WIKI_WEB_HOME],
  )

  const entry = useMemo(() => {
    if (!catalog || !slug) return undefined
    return catalog.docs.find((d) => d.slug === slug)
  }, [catalog, slug])

  useEffect(() => {
    setContent('')
    setError('')
    if (!slug) return
    if (loading) return

    if (entry?.path) {
      const p = entry.path
      const isHttp = /^https?:\/\//i.test(p)
      const isRawMarkdown = /\.md($|\?)/i.test(p)
      const isInternalRoute = p.startsWith('/docs/') && !isRawMarkdown

      if (isInternalRoute) {
        setRedirectTo(p)
        return
      }
      if (isHttp || isRawMarkdown) {
        fetch(p)
          .then((res) => {
            if (!res.ok) throw new Error(`Could not load ${p}`)
            return res.text()
          })
          .then((text) => setContent(preprocessWikiSyntax(text)))
          .catch((e) => setError(e.message))
        return
      }
    }

    ;(async () => {
      const result = await loadWikiPage(slug)
      if (result) setContent(preprocessWikiSyntax(result.text))
      else setError('Page not found on source (raw.githubusercontent.com/wiki)')
    })()
  }, [entry, slug, loading])

  if (loading) return <p>Loading…</p>
  if (catError) return <div className="card" role="alert">Failed to load catalog: {catError}</div>
  if (redirectTo) return <Navigate to={redirectTo} replace/>

  return (
    <section className="section doc-standalone">
      <div className="doc-standalone-inner">
        <div className="doc-standalone-main">
          <div className="back-row">
            <Link to="/docs" className="back-link">← Back to docs</Link>
          </div>
          <h1 className="page-title">{entry?.title || String(slug)}</h1>
          {entry?.summary && <p className="page-subtitle"><em>{entry.summary}</em></p>}
          {error && (
            <div className="card" role="alert">
              <strong>Not found.</strong> {error}
            </div>
          )}
          {!error && content && (
            <div className="prose" ref={proseRef}>
              <MarkdownRenderer markdown={content} decideLink={decideLink}/>
            </div>
          )}
        </div>
        {!error && content && <OnThisPage containerRef={proseRef} watch={content}/>}
      </div>
    </section>
  )
}
